import React, { useCallback, useMemo, useState } from "react";
import { Box, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { styled } from "@mui/styles";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gaps,
  padding,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import StewartInput from "controls/global/stewart-input";
import StewartAutocompleteWithCheckboxes, {
  OptionRenderType,
} from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import { PremiumReceivablesAgentRecord } from "entities/UIModel/PremiumReceivablesAgentRecord";
import { PremiumReceivablesTransactionRecord } from "entities/UIModel/PremiumReceivablesTransactionRecord";
import { PremiumReceivablesReceiptRecord } from "entities/UIModel/PremiumReceivablesReceiptRecord";
import { toPeriodCode } from "../shared/formatters";
import LedgerReportBody, { LedgerBlock } from "./LedgerReportBody";
import { GroupBy, DetailLevel, BalanceFilter, groupByLabel } from "./ledgerReportTypes";

const TabContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.medium1,
});

const CriteriaContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.medium1,
  padding: padding.large1,
  backgroundColor: colors.white,
  borderRadius: borderRadius.small,
  border: `1px solid ${colors.grey09}`,
});

const CriteriaRow = styled(Box)({
  display: "flex",
  gap: gaps.medium1,
  flexWrap: "wrap",
  alignItems: "flex-end",
});

const CriteriaField = styled(Box)({
  flex: "1 1 200px",
  minWidth: "180px",
  maxWidth: "280px",
  "& .MuiInputBase-root": {
    height: "40px",
  },
});

const SectionTitle = styled(Typography)({
  fontSize: fontSize.large,
  fontWeight: fontWeight.bold2,
  color: colors.blue15,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  marginBottom: "4px",
});

const IncludeRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.large1,
  padding: `${padding.small1} ${padding.medium1}`,
  backgroundColor: colors.grey10,
  border: `1px solid ${colors.grey09}`,
  borderRadius: borderRadius.small,
});

const ParamsHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.small1,
  cursor: "pointer",
  "& .chev": {
    color: colors.grey11,
    display: "flex",
  },
  "& .title": {
    fontSize: fontSize.large,
    fontWeight: fontWeight.bold2,
    color: colors.blue15,
    whiteSpace: "nowrap",
  },
  "& .hint": {
    marginLeft: "auto",
    fontSize: fontSize.xsmall,
    color: colors.grey11,
  },
});

const SummaryChipStrip = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: gaps.xsmall1,
});

const SummaryChip = styled(Box)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.normal2,
  color: colors.blue02,
  backgroundColor: colors.blue08,
  border: `1px solid ${colors.blue01}`,
  borderRadius: borderRadius.xlarge1,
  padding: "3px 10px",
  whiteSpace: "nowrap",
});

interface LedgerReportTabProps {
  agents: PremiumReceivablesAgentRecord[];
  transactions: PremiumReceivablesTransactionRecord[];
  receipts: PremiumReceivablesReceiptRecord[];
}

const LedgerReportTab = ({ agents, transactions, receipts }: LedgerReportTabProps) => {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [acctPeriodFrom, setAcctPeriodFrom] = useState("");
  const [acctPeriodTo, setAcctPeriodTo] = useState("");
  const [selectedDivisions, setSelectedDivisions] = useState<OptionRenderType[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<OptionRenderType[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<OptionRenderType[]>([]);
  const [selectedStates, setSelectedStates] = useState<OptionRenderType[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<OptionRenderType[]>([]);
  const [groupBy, setGroupBy] = useState<GroupBy>("agent");
  const [detailLevel, setDetailLevel] = useState<DetailLevel>("detail");
  const [balanceFilter, setBalanceFilter] = useState<BalanceFilter>("all");
  const [incAccruals, setIncAccruals] = useState(false);
  const [incCancelled, setIncCancelled] = useState(false);
  const [incNonIssuing, setIncNonIssuing] = useState(false);
  const [ledgerData, setLedgerData] = useState<LedgerBlock[] | null>(null);
  const [subtitle, setSubtitle] = useState("");
  const [summaryChips, setSummaryChips] = useState<string[]>([]);
  // Expanded on first arrival (no report yet); collapses to a chip summary
  // once a report has been generated, matching the mockup's params section.
  const [paramsCollapsed, setParamsCollapsed] = useState(false);
  // Bumped on Reset so each StewartAutocompleteWithCheckboxes remounts —
  // it keeps its own internal, uncontrolled selection state and only reads
  // defaultSelectedOption once on mount, so it won't visually clear just
  // because the parent's selected* state was reset (see AdvancedSearchPanel
  // for the same workaround).
  const [resetKey, setResetKey] = useState(0);
  // Same remount trick, scoped to the cascading Region/District fields: a
  // Division change clears Region+District state, and a Region change clears
  // District state, but those fields need their own key bump too or they'd
  // keep showing their stale chips despite the parent state being cleared.
  const [regionKey, setRegionKey] = useState(0);
  const [districtKey, setDistrictKey] = useState(0);

  const toOptions = (values: (string | undefined)[]) =>
    [...new Set(values.filter((v): v is string => !!v))].sort().map((v) => ({ label: v, value: v }));

  // Options
  const divisionOptions = useMemo(() => toOptions(agents.map((a) => a.division)), [agents]);

  // Cascading: Region narrows to the selected Division(s); District narrows to
  // the selected Division(s) + Region(s) — matches the mockup's onLedgerOrgChange.
  const regionOptions = useMemo(() => {
    const divisionValues = selectedDivisions.map((v) => v.value);
    const scoped = divisionValues.length ? agents.filter((a) => divisionValues.includes(a.division)) : agents;
    return toOptions(scoped.map((a) => a.region));
  }, [agents, selectedDivisions]);

  const districtOptions = useMemo(() => {
    const divisionValues = selectedDivisions.map((v) => v.value);
    const regionValues = selectedRegions.map((v) => v.value);
    let scoped = agents;
    if (divisionValues.length) scoped = scoped.filter((a) => divisionValues.includes(a.division));
    if (regionValues.length) scoped = scoped.filter((a) => regionValues.includes(a.region));
    return toOptions(scoped.map((a) => a.district));
  }, [agents, selectedDivisions, selectedRegions]);

  const stateOptions = useMemo(() => toOptions(agents.map((a) => a.state)), [agents]);

  const agentIDOptions = useMemo(() => {
    return agents
      .map((a) => ({ label: `${a.agentID} — ${a.name}`, value: a.agentID }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [agents]);

  // Accounting periods seen in the data, most recent first, offered as
  // suggestions on the Acct Period From/To inputs (mockup's ledger-ap-options
  // datalist).
  const acctPeriodOptions = useMemo(() => {
    const unique = [...new Set(transactions.map((t) => t.AccountingPeriod).filter((v): v is string => !!v))];
    return unique.sort().reverse();
  }, [transactions]);

  const handleDivisionChange = useCallback((_e: unknown, value: OptionRenderType[]) => {
    setSelectedDivisions(value);
    // Downstream selections may no longer be valid under the narrowed scope.
    setSelectedRegions([]);
    setSelectedDistricts([]);
    setRegionKey((k) => k + 1);
    setDistrictKey((k) => k + 1);
  }, []);

  const handleRegionChange = useCallback((_e: unknown, value: OptionRenderType[]) => {
    setSelectedRegions(value);
    setSelectedDistricts([]);
    setDistrictKey((k) => k + 1);
  }, []);

  const handleGenerate = useCallback(() => {
    const apFrom = toPeriodCode(acctPeriodFrom) || (acctPeriodFrom.replace(/\D/g, "").slice(0, 6) || null);
    const apTo = toPeriodCode(acctPeriodTo) || (acctPeriodTo.replace(/\D/g, "").slice(0, 6) || null);

    // Transactions carry an ISO ReceivedDate; receipts carry MM/DD/YYYY. Both
    // are normalized to YYYY-MM-DD so they compare against the <input type="date">
    // values (also YYYY-MM-DD) lexicographically.
    const toYmd = (value: string | null | undefined): string | null => {
      if (!value) return null;
      if (value.includes("/")) {
        const [m, d, y] = value.split("/");
        if (!m || !d || !y) return null;
        return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
      }
      return value.slice(0, 10);
    };
    const inDateRange = (rawDate: string | null | undefined) => {
      const ymd = toYmd(rawDate);
      if (!ymd) return !dateFrom && !dateTo;
      if (dateFrom && ymd < dateFrom) return false;
      if (dateTo && ymd > dateTo) return false;
      return true;
    };
    const beforeDateFrom = (rawDate: string | null | undefined) => {
      const ymd = toYmd(rawDate);
      if (!dateFrom || !ymd) return false;
      return ymd < dateFrom;
    };
    const inAcctPeriod = (period: string | null | undefined) => {
      if (!apFrom && !apTo) return true;
      if (!period) return false;
      if (apFrom && period < apFrom) return false;
      if (apTo && period > apTo) return false;
      return true;
    };
    const isAccrual = (t: PremiumReceivablesTransactionRecord) => !!t.IsAccrual;

    // Filter agents
    let filteredAgents = agents;
    if (selectedDivisions.length > 0) {
      const values = selectedDivisions.map((v) => v.value);
      filteredAgents = filteredAgents.filter((a) => values.includes(a.division));
    }
    if (selectedRegions.length > 0) {
      const values = selectedRegions.map((v) => v.value);
      filteredAgents = filteredAgents.filter((a) => values.includes(a.region));
    }
    if (selectedDistricts.length > 0) {
      const values = selectedDistricts.map((v) => v.value);
      filteredAgents = filteredAgents.filter((a) => values.includes(a.district));
    }
    if (selectedStates.length > 0) {
      const values = selectedStates.map((v) => v.value);
      filteredAgents = filteredAgents.filter((a) => values.includes(a.state));
    }
    if (selectedAgents.length > 0) {
      const values = selectedAgents.map((v) => v.value);
      filteredAgents = filteredAgents.filter((a) => values.includes(a.agentID));
    }
    if (!incCancelled) filteredAgents = filteredAgents.filter((a) => !a.isCancelled);
    if (!incNonIssuing) filteredAgents = filteredAgents.filter((a) => !a.isNonIssuing);

    // Build ledger blocks
    const blocks: LedgerBlock[] = filteredAgents.map((agent) => {
      const allPR = transactions.filter(
        (t) => t.AgentID === agent.agentID && (incAccruals || !isAccrual(t))
      );
      const allAR = receipts.filter((r) => r.AgentID === agent.agentID);

      const periodPR = allPR
        .filter((t) => inDateRange(t.ReceivedDate) && inAcctPeriod(t.AccountingPeriod))
        .sort((x, y) => String(x.ReceivedDate).localeCompare(String(y.ReceivedDate)));
      const periodAR = allAR
        .filter((r) => inDateRange(r.ReceiptDate))
        .sort((x, y) => String(x.ReceiptDate).localeCompare(String(y.ReceiptDate)));

      const prBefore = allPR.filter((t) => beforeDateFrom(t.ReceivedDate));
      const arBefore = allAR.filter((r) => beforeDateFrom(r.ReceiptDate));
      const beginBal =
        agent.beginBal +
        prBefore.reduce((s, t) => s + (t.ActualRemittance || 0), 0) +
        arBefore.reduce((s, r) => s + (r.ReceiptAmount || 0), 0);

      const totalCharges = periodPR.reduce((sum, t) => sum + (t.ActualRemittance || 0), 0);
      const totalPayments = periodAR.reduce((sum, r) => sum + (r.ReceiptAmount || 0), 0);
      return {
        agent,
        beginBal,
        periodPR,
        periodAR,
        totalCharges,
        totalPayments,
        endBal: beginBal + totalCharges + totalPayments,
      };
    })
      // Drop agents with no activity in scope and an effectively zero balance.
      .filter((b) => b.periodPR.length || b.periodAR.length || Math.abs(b.beginBal) >= 0.01);

    // Apply balance filter
    let filtered = blocks;
    if (balanceFilter === "debit") {
      filtered = blocks.filter((b) => b.endBal > 0);
    } else if (balanceFilter === "credit") {
      filtered = blocks.filter((b) => b.endBal < 0);
    }

    setLedgerData(filtered);

    const parts: string[] = [];
    parts.push(dateFrom || dateTo ? `${dateFrom || "*"} → ${dateTo || "*"}` : "All Dates");
    if (apFrom || apTo) parts.push(`AP ${apFrom || "*"} → ${apTo || "*"}`);
    if (selectedStates.length) parts.push(`State${selectedStates.length > 1 ? "s" : ""}: ${selectedStates.map((s) => s.value).join(", ")}`);
    if (selectedDivisions.length) parts.push(`Division: ${selectedDivisions.map((s) => s.value).join(", ")}`);
    if (selectedRegions.length) parts.push(`Region: ${selectedRegions.map((s) => s.value).join(", ")}`);
    if (selectedDistricts.length) parts.push(`District: ${selectedDistricts.map((s) => s.value).join(", ")}`);
    if (selectedAgents.length) parts.push(`${selectedAgents.length} agent${selectedAgents.length > 1 ? "s" : ""} selected`);
    if (balanceFilter === "debit") parts.push("Debit Balances Only");
    if (balanceFilter === "credit") parts.push("Credit Balances Only");
    if (detailLevel === "totals") parts.push("Totals Only");
    parts.push(groupByLabel[groupBy]);
    const finalParts = parts.filter(Boolean);
    setSubtitle(finalParts.join(" · "));
    setSummaryChips(finalParts);
    setParamsCollapsed(true);
  }, [
    agents,
    transactions,
    receipts,
    dateFrom,
    dateTo,
    acctPeriodFrom,
    acctPeriodTo,
    selectedDivisions,
    selectedRegions,
    selectedDistricts,
    selectedStates,
    selectedAgents,
    balanceFilter,
    detailLevel,
    groupBy,
    incAccruals,
    incCancelled,
    incNonIssuing,
  ]);

  const handleClear = useCallback(() => {
    setLedgerData(null);
    setSubtitle("");
    setSummaryChips([]);
    setParamsCollapsed(false);
  }, []);

  const handleResetFilters = useCallback(() => {
    setDateFrom("");
    setDateTo("");
    setAcctPeriodFrom("");
    setAcctPeriodTo("");
    setSelectedDivisions([]);
    setSelectedRegions([]);
    setSelectedDistricts([]);
    setSelectedStates([]);
    setSelectedAgents([]);
    setGroupBy("agent");
    setDetailLevel("detail");
    setBalanceFilter("all");
    setIncAccruals(false);
    setIncCancelled(false);
    setIncNonIssuing(false);
    setLedgerData(null);
    setSubtitle("");
    setSummaryChips([]);
    setParamsCollapsed(false);
    setResetKey((k) => k + 1);
    setRegionKey((k) => k + 1);
    setDistrictKey((k) => k + 1);
  }, []);

  const toggleParamsSection = useCallback(() => {
    setParamsCollapsed((prev) => !prev);
  }, []);

  return (
    <TabContainer>
      {/* Criteria */}
      <CriteriaContainer>
        <ParamsHeader onClick={toggleParamsSection}>
          <span className="chev">
            {paramsCollapsed ? <ChevronRightIcon /> : <ExpandMoreIcon />}
          </span>
          <span className="title">Report Parameters</span>
          {paramsCollapsed && (
            <SummaryChipStrip>
              {summaryChips.map((chip) => (
                <SummaryChip key={chip}>{chip}</SummaryChip>
              ))}
            </SummaryChipStrip>
          )}
          <span className="hint">{paramsCollapsed ? "Click to edit and regenerate" : ""}</span>
        </ParamsHeader>

        {!paramsCollapsed && (
          <>
        <SectionTitle>Filter Period</SectionTitle>
        <CriteriaRow>
          <CriteriaField>
            <StewartInput
              name="dateFrom"
              id="dateFrom"
              label="Received Date From"
              type="date"
              value={dateFrom}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </CriteriaField>
          <CriteriaField>
            <StewartInput
              name="dateTo"
              id="dateTo"
              label="Received Date To"
              type="date"
              value={dateTo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </CriteriaField>
          <CriteriaField>
            <StewartInput
              name="acctPeriodFrom"
              id="acctPeriodFrom"
              label="Acct Period From"
              value={acctPeriodFrom}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAcctPeriodFrom(e.target.value)}
              InputLabelProps={{ shrink: !!acctPeriodFrom }}
              inputProps={{ maxLength: 6, placeholder: "YYYYMM", list: "ledger-ap-options" }}
              fullWidth
            />
          </CriteriaField>
          <CriteriaField>
            <StewartInput
              name="acctPeriodTo"
              id="acctPeriodTo"
              label="Acct Period To"
              value={acctPeriodTo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAcctPeriodTo(e.target.value)}
              InputLabelProps={{ shrink: !!acctPeriodTo }}
              inputProps={{ maxLength: 6, placeholder: "YYYYMM", list: "ledger-ap-options" }}
              fullWidth
            />
          </CriteriaField>
        </CriteriaRow>
        <datalist id="ledger-ap-options">
          {acctPeriodOptions.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>

        <SectionTitle>Organization</SectionTitle>
        <CriteriaRow>
          <CriteriaField>
            <StewartAutocompleteWithCheckboxes
              key={`division-${resetKey}`}
              name="division"
              placeholder="Division"
              options={divisionOptions}
              defaultSelectedOption={selectedDivisions}
              onChange={handleDivisionChange}
            />
          </CriteriaField>
          <CriteriaField>
            <StewartAutocompleteWithCheckboxes
              key={`region-${resetKey}-${regionKey}`}
              name="region"
              placeholder="Region"
              options={regionOptions}
              defaultSelectedOption={selectedRegions}
              onChange={handleRegionChange}
            />
          </CriteriaField>
          <CriteriaField>
            <StewartAutocompleteWithCheckboxes
              key={`district-${resetKey}-${districtKey}`}
              name="district"
              placeholder="District"
              options={districtOptions}
              defaultSelectedOption={selectedDistricts}
              onChange={(_e, value) => setSelectedDistricts(value)}
            />
          </CriteriaField>
          <CriteriaField>
            <StewartAutocompleteWithCheckboxes
              key={`state-${resetKey}`}
              name="state"
              placeholder="State(s)"
              options={stateOptions}
              defaultSelectedOption={selectedStates}
              onChange={(_e, value) => setSelectedStates(value)}
            />
          </CriteriaField>
          <CriteriaField>
            <StewartAutocompleteWithCheckboxes
              key={`agent-${resetKey}`}
              name="agent"
              placeholder="Agent(s)"
              options={agentIDOptions}
              defaultSelectedOption={selectedAgents}
              onChange={(_e, value) => setSelectedAgents(value)}
            />
          </CriteriaField>
        </CriteriaRow>

        <SectionTitle>Output Options</SectionTitle>
        <CriteriaRow>
          <CriteriaField>
            <FormControl fullWidth>
              <InputLabel id="groupBy-label" shrink>Group / Scope</InputLabel>
              <Select
                labelId="groupBy-label"
                label="Group / Scope"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              >
                <MenuItem value="agent">By Agent</MenuItem>
                <MenuItem value="state">By State</MenuItem>
                <MenuItem value="division">By Division</MenuItem>
                <MenuItem value="region">By Region</MenuItem>
                <MenuItem value="district">By District</MenuItem>
                <MenuItem value="all">All — Grand Summary</MenuItem>
              </Select>
            </FormControl>
          </CriteriaField>
          <CriteriaField>
            <FormControl fullWidth>
              <InputLabel id="detailLevel-label" shrink>Detail Level</InputLabel>
              <Select
                labelId="detailLevel-label"
                label="Detail Level"
                value={detailLevel}
                onChange={(e) => setDetailLevel(e.target.value as DetailLevel)}
              >
                <MenuItem value="detail">With Detail</MenuItem>
                <MenuItem value="totals">Totals Only</MenuItem>
              </Select>
            </FormControl>
          </CriteriaField>
          <CriteriaField>
            <FormControl fullWidth>
              <InputLabel id="balanceFilter-label" shrink>Balance Filter</InputLabel>
              <Select
                labelId="balanceFilter-label"
                label="Balance Filter"
                value={balanceFilter}
                onChange={(e) => setBalanceFilter(e.target.value as BalanceFilter)}
              >
                <MenuItem value="all">All Balances</MenuItem>
                <MenuItem value="debit">Debit Balances Only</MenuItem>
                <MenuItem value="credit">Credit Balances Only</MenuItem>
              </Select>
            </FormControl>
          </CriteriaField>
        </CriteriaRow>

        <IncludeRow>
          <Typography sx={{ fontSize: fontSize.xsmall, color: colors.grey11, fontWeight: fontWeight.bold2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Include
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={incAccruals} onChange={(e) => setIncAccruals(e.target.checked)} size="small" />}
            label="Accruals"
          />
          <FormControlLabel
            control={<Checkbox checked={incCancelled} onChange={(e) => setIncCancelled(e.target.checked)} size="small" />}
            label="Cancelled Agents"
          />
          <FormControlLabel
            control={<Checkbox checked={incNonIssuing} onChange={(e) => setIncNonIssuing(e.target.checked)} size="small" />}
            label="Non-Issuing Agents"
          />
        </IncludeRow>

        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: gaps.small1 }}>
          <ActionButton variant="outlined" color="secondary" onClick={handleResetFilters}>
            Reset All Filters
          </ActionButton>
        </Box>
          </>
        )}
      </CriteriaContainer>

      {/* Action buttons */}
      <Box sx={{ display: "flex", alignItems: "center", gap: gaps.small1, justifyContent: "flex-end" }}>
        <ActionButton variant="outlined" color="secondary" onClick={handleClear} disabled={!ledgerData}>
          Clear
        </ActionButton>
        <ActionButton variant="outlined" color="primary" onClick={handleGenerate}>
          Generate Ledger
        </ActionButton>
      </Box>

      <LedgerReportBody ledgerData={ledgerData} groupBy={groupBy} detailLevel={detailLevel} subtitle={subtitle} />
    </TabContainer>
  );
};

export default LedgerReportTab;
